import { Link2Off, Loader2 } from "lucide-react";
import { useEffect } from "react";
import { Outlet, useParams } from "react-router";

import { PreviewExperience } from "@/components/sku/preview-experience";
import { SidebarProvider } from "@/components/ui/sidebar";
import { CMSHeader } from "@/components/web-experience/cms-header";
import { CMSSidebar } from "@/components/web-experience/cms-sidebar";
import { useGetSKUQuery } from "@/data/queries";
import { useCMSStore } from "@/hooks/use-cms-store";
import {
  combinePageContentToSKU,
  combineUniqueContentToSKU,
  getPreviewParamsFromSKU,
} from "@/lib/preview";

export const Layout = () => {
  const { skuId } = useParams<{ skuId: string }>();
  const { isPending, data: sku } = useGetSKUQuery(skuId!);
  const pageContent = useCMSStore.use.pageContent();
  const uniqueContent = useCMSStore.use.uniqueContent();
  const setPageContent = useCMSStore.use.setPageContent();
  const setUniqueContent = useCMSStore.use.setUniqueContent();

  useEffect(() => {
    if (sku) {
      setPageContent(sku.page_content);
      setUniqueContent(sku.unique_content);
    }
  }, [
    setPageContent,
    sku,
    sku?.page_content,
    sku?.unique_content,
    setUniqueContent,
  ]);

  if (isPending) {
    return (
      <div className="h-screen flex items-center justify-center gap-2">
        <Loader2 className="size-6 animate-spin" />
        <span>Loading SKU...</span>
      </div>
    );
  }

  if (!sku) {
    return (
      <div className="h-screen flex items-center justify-center gap-2">
        <Link2Off className="size-6" />
        <span>SKU not found</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen max-h-screen flex flex-col">
      <CMSHeader skuId={sku.id} skuName={sku.name} />
      <SidebarProvider className="min-h-full grow shrink">
        <CMSSidebar />
        <Outlet />
        <div className="p-12">
          <PreviewExperience
            params={getPreviewParamsFromSKU(
              combineUniqueContentToSKU(
                combinePageContentToSKU(sku, pageContent),
                uniqueContent,
              ),
            )}
          />
        </div>
      </SidebarProvider>
    </div>
  );
};
